// store.ts
import {
  configureStore,
  combineReducers,
  ThunkDispatch,
  AnyAction,
  PreloadedState,
} from '@reduxjs/toolkit'

import { api } from '../services/api'
import orderReducers from 'modules/order/state/order/order-reducers'
import {
  recurringNudgeStepMiddleware,
  flowCurrentStepListener,
  flowOrderPaymentMethodListener,
  paymentMethodListenerMiddleware,
} from 'modules/flow/state/flow-middlewares'
import drawerSlice from 'modules/drawer/state/drawer-slice'
import flowSlice from 'modules/flow/state/flow-slice'
import configSlice from 'modules/config/state/config-slice'
import statusSlice from 'modules/status/state/status-slice'
import urlParamsSlice from 'modules/url-params/state/app-url-params-slice'
import { donorCoveredFeesMiddleware } from 'modules/order/state/dcf/order-donor-covered-fees-middlewares'
import { sdkDonationChangedMiddleware } from 'sdk/utils/sdk-middleware'

const baseReducer = combineReducers({
  order: orderReducers,
  flow: flowSlice.reducer,
  drawer: drawerSlice.reducer,
  config: configSlice.reducer,
  status: statusSlice.reducer,
  urlParams: urlParamsSlice.reducer,
  [api.reducerPath]: api.reducer,
})

export type RootState = ReturnType<typeof baseReducer>
export type TypedDispatch<T> = ThunkDispatch<T, unknown, AnyAction>

let store: ReturnType<typeof makeStore> | undefined

export const makeStore = (preloadedState?: PreloadedState<RootState>) =>
  configureStore({
    devTools: true,
    reducer: baseReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .prepend(recurringNudgeStepMiddleware.middleware)
        .prepend(flowOrderPaymentMethodListener.middleware)
        .prepend(flowCurrentStepListener.middleware)
        .prepend(donorCoveredFeesMiddleware.middleware)
        .prepend(paymentMethodListenerMiddleware.middleware)
        .prepend(sdkDonationChangedMiddleware.middleware)
        .concat(api.middleware),
  })

/**
 * Reset the store to undefined, forcing a new store to be created on next initializeStore call.
 * This is useful for testing to ensure test isolation.
 */
export const resetStore = () => {
  store = undefined
}

// Conditional initializer that merges state
export const initializeStore = (preloadedState?: PreloadedState<RootState>) => {
  // If there is preloaded state and an existing store, merge them
  if (preloadedState && store) {
    const existingState = store.getState()

    store = makeStore({
      ...existingState,
      ...preloadedState,
      config: existingState.config ?? preloadedState.config,
    })
  }

  // Always create the store if it's not already
  if (!store) {
    store = makeStore(preloadedState)
  }

  return store
}
